import type { Dispatch, SetStateAction } from 'react';
import { Fragment } from 'react';

import { Link, useParams } from 'react-router';

import { Bars2Icon, MagnifyingGlassIcon, ShoppingBagIcon } from '@heroicons/react/24/outline';

import { useCart } from '@boc/framework/checkout/cart/provider';

import { cn } from '~/styling';
import { urls } from '~/urls';

import CountrySelector from '~/local/components/country-selector';
import { JigsawIcon } from '~/local/components/icons';

import { brandInfo } from '#/brand';
import type { Navigation } from '#/brand/navigation';

export default function Header({
  setMobileMenuOpen,
  setShowSearch,
  navigation,
}: {
  setMobileMenuOpen: Dispatch<SetStateAction<boolean>> | null;
  showSearch: boolean;
  setShowSearch: Dispatch<SetStateAction<boolean>>;
  navigation: Navigation[];
}) {
  const params = useParams();

  const { isEmpty } = useCart();

  const itemsInCart = !isEmpty;

  return (
    <header className={cn('text-primary-2-80 flex h-full w-full items-center overflow-visible')}>
      <div className="border-neutral-2-20 container h-full w-full max-w-screen-xl overflow-visible border-b px-6 lg:border-0 xl:mx-auto">
        <div className="border-neutral-2-20 flex h-full w-full items-center overflow-visible lg:border-b">
          <div className="mx-auto flex flex-1">
            {setMobileMenuOpen ? (
              <button className="md:hidden" onClick={() => setMobileMenuOpen(true)}>
                <Bars2Icon className="h-6 rounded-full p-1" />
              </button>
            ) : null}
            <div className="font-heading hidden gap-x-3 text-base leading-4 font-medium tracking-tight md:flex">
              {navigation?.map((item, index) => (
                <Fragment key={`nav-${index}`}>
                  <Link className="place-self-center" to={item.to(params)}>
                    {item.title}
                  </Link>
                  {index < navigation.length - 1 && <JigsawIcon />}
                </Fragment>
              ))}
            </div>
          </div>
          <div className="mx-6 flex sm:mx-12">
            <Link to={urls.home(params)}>
              <img
                className="h-8"
                src={brandInfo.logo?.svg}
                alt={[brandInfo.name, brandInfo.tagline].join(' | ')}
              />
            </Link>
          </div>
          <menu className="mx-auto flex flex-1 items-center justify-end gap-x-3 overflow-visible md:gap-x-4">
            <CountrySelector />
            <button
              className="cursor-pointer"
              onClick={() => {
                setShowSearch(true);
              }}
            >
              <MagnifyingGlassIcon className="h-4 sm:h-5" />
            </button>
            <Link className="relative flex items-center" to={urls.checkout.cart(params)}>
              <ShoppingBagIcon className="h-4 sm:h-5" />
              {itemsInCart && (
                <span className="bg-primary-2-60 absolute top-0 right-0 -mt-1 -mr-2 inline-flex h-3 w-3 rounded-full"></span>
              )}
            </Link>
          </menu>
        </div>
      </div>
    </header>
  );
}
